import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  Typography,
  InputAdornment,
  Divider,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useRBAC } from '../../auth/useRBAC';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { statementSettingsApi, StatementSettings, invoiceSettingsApi, InvoiceSettings } from '../../utils/api';
import { useIsOffline } from '../../offline';
import PaymentTypesSettings from './PaymentTypesSettings';
import { useSnackbar } from 'notistack';
import { useFormatInstant } from '../../hooks/useClinicTimezone';

/**
 * One home for everything billing: the invoice defaults that used to sit at
 * the bottom of Practice settings (tax rate / tax mode / payment due) and the
 * per-clinic payment-type manager that used to be its own settings section.
 * Practice settings is back to practice identity only.
 */
const BillingSettings: React.FC = () => {
  const { claims, loading: claimsLoading } = useRBAC();
  const isOffline = useIsOffline();
  const { enqueueSnackbar } = useSnackbar();
  const formatInstant = useFormatInstant();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    taxRate: '',
    taxMode: 'ALL',
    defaultPaymentDays: '30',
  });

  const isEditable = claims?.role === 'ADMIN' || claims?.role === 'OWNER';

  const { data: clinicData, isLoading: clinicLoading } = useQuery(
    ['clinic', claims?.clinicId],
    async () => {
      if (!claims?.clinicId) return null;
      const response = await api.get(`/clinics/${claims.clinicId}`);
      return response.data;
    },
    {
      enabled: !!claims?.clinicId,
    }
  );

  // Hydrate from the query DATA, not onSuccess — onSuccess never fires for a
  // cache-served remount (fresh within staleTime). Here that isn't just
  // cosmetic: an un-hydrated form shows taxRate '' and Save would write
  // taxRate: 0, silently zeroing the clinic's tax rate.
  useEffect(() => {
    if (!clinicData) return;
    setFormData({
      taxRate: clinicData.taxRate !== undefined && clinicData.taxRate !== null ? String(clinicData.taxRate) : '',
      taxMode: clinicData.taxMode || 'ALL',
      defaultPaymentDays: clinicData.defaultPaymentDays != null ? String(clinicData.defaultPaymentDays) : '30',
    });
  }, [clinicData]);

  const updateMutation = useMutation(
    // Partial update: the clinic endpoint applies only the provided fields,
    // so the practice-identity fields are untouched from here.
    (data: Record<string, any>) => api.put(`/clinics/${claims?.clinicId}`, data),
    {
      onSuccess: () => {
        // The prefix covers ['clinic','current'], which every screen formats times with.
        queryClient.invalidateQueries(['clinic']);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      },
    }
  );

  // Hospital auto-bill: applies the moment it is switched, so it gets its own
  // mutation + snackbar rather than riding the "Save Changes" button above.
  // The server owns the cutover instant — we only ever send the boolean.
  const autoBillMutation = useMutation(
    (enabled: boolean) => api.put(`/clinics/${claims?.clinicId}`, { hospitalAutoBillEnabled: enabled }),
    {
      onSuccess: (_res, enabled) => {
        // The prefix covers ['clinic','current'], which every screen formats times with.
        queryClient.invalidateQueries(['clinic']);
        enqueueSnackbar(
          enabled
            ? 'Hospital treatments will now be billed automatically'
            : 'Automatic hospital billing turned off',
          { variant: 'success' },
        );
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.response?.data?.message || 'Could not change automatic hospital billing',
          { variant: 'error' },
        );
      },
    },
  );

  const autoBillOn = !!clinicData?.hospitalAutoBillEnabled;
  // Clinic.hospitalAutoBillEnabledAt is an INSTANT — a write-once `new Date()`
  // stamp that doubles as the no-retro-billing fence (DATE-KINDS.md 1.3). Never
  // date-parted, so that half of the old comment stands. What changed is the
  // ZONE: the fence this stamp names is evaluated against the CLINIC's day in
  // treatment-billing.service.ts (`clinicLocalDateStr`), so a browser-zone
  // render could advertise a different switch-on date than billing enforces.
  const autoBillOnSince = clinicData?.hospitalAutoBillEnabledAt
    ? formatInstant(clinicData.hospitalAutoBillEnabledAt, 'MMMM d, yyyy')
    : null;

  // Monthly statements: its own validated endpoint pair (not the clinics PUT).
  const [stmtForm, setStmtForm] = useState({ enabled: false, coverLetter: '', taxNumber: '', terms: '' });
  const { data: stmtSettings } = useQuery<StatementSettings>(
    ['statement-settings'],
    () => statementSettingsApi.get(),
    { enabled: !!claims?.clinicId },
  );

  // Hydrate from the query DATA (same rationale as the clinic form above).
  useEffect(() => {
    if (!stmtSettings) return;
    setStmtForm({
      enabled: !!stmtSettings.monthlyStatementsEnabled,
      coverLetter: stmtSettings.statementCoverLetter ?? '',
      taxNumber: stmtSettings.taxRegistrationNumber ?? '',
      terms: stmtSettings.statementTerms ?? '',
    });
  }, [stmtSettings]);

  const stmtMutation = useMutation(
    (data: {
      monthlyStatementsEnabled: boolean;
      statementCoverLetter: string;
      taxRegistrationNumber: string;
      statementTerms: string;
    }) => statementSettingsApi.update(data),
    {
      onSuccess: (saved) => {
        queryClient.setQueryData(['statement-settings'], saved);
        enqueueSnackbar('Monthly statement settings saved', { variant: 'success' });
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Could not save statement settings', { variant: 'error' });
      },
    },
  );

  // The note every new invoice starts with (ADR-0044): its own endpoint, because the server
  // audits every change (it can hold a Venmo link), and the invoice form reads the same query.
  const [noteDraft, setNoteDraft] = useState('');
  const { data: invoiceSettings } = useQuery<InvoiceSettings>(
    ['invoice-settings'],
    () => invoiceSettingsApi.get(),
    { enabled: !!claims?.clinicId },
  );

  // Hydrate from the query DATA (same rationale as the clinic form above).
  useEffect(() => {
    if (!invoiceSettings) return;
    setNoteDraft(invoiceSettings.defaultClientNote ?? '');
  }, [invoiceSettings]);

  const noteMutation = useMutation(
    (defaultClientNote: string) => invoiceSettingsApi.update({ defaultClientNote }),
    {
      onSuccess: (saved) => {
        queryClient.setQueryData(['invoice-settings'], saved);
        enqueueSnackbar('Default invoice note saved', { variant: 'success' });
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.response?.data?.message || 'Could not save the default invoice note', { variant: 'error' });
      },
    },
  );

  const handleSave = () => {
    if (!isEditable) return;
    updateMutation.mutate({
      taxRate: formData.taxRate ? parseFloat(formData.taxRate) : 0,
      taxMode: formData.taxMode,
      defaultPaymentDays: formData.defaultPaymentDays ? parseInt(formData.defaultPaymentDays, 10) : 30,
    });
  };

  if (claimsLoading || clinicLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  // Hard gate, not read-only: this page carries the refund-approval PIN and
  // payment-type configuration. The tab is already hidden for non-admins in
  // Settings.tsx; this guard covers direct renders/deep links (UserManagement
  // pattern). Backend @Roles on the write endpoints is the real enforcement.
  if (!isEditable) {
    return (
      <Box p={3}>
        <Typography>Only admins and owners can access invoicing settings.</Typography>
      </Box>
    );
  }

  const readOnlyProps = isEditable
    ? {}
    : {
        InputProps: { readOnly: true },
      };

  return (
    <Box>
      {showSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Billing defaults updated.
        </Alert>
      )}

      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Billing Defaults
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Default values pre-filled when creating new invoices.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, maxWidth: 650 }}>
        <TextField
          label="Default Tax Rate"
          type="number"
          value={formData.taxRate}
          onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 100, step: 0.1 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText="Pre-filled on new invoices."
          {...readOnlyProps}
        />

        <TextField
          select
          label="Tax Applies To"
          value={formData.taxMode}
          onChange={(e) => setFormData({ ...formData, taxMode: e.target.value })}
          size="small"
          fullWidth
          helperText="Choose which line item types are taxed. Custom items not from your service catalog are treated as services."
          disabled={!isEditable}
        >
          <MenuItem value="ALL">All charges</MenuItem>
          <MenuItem value="SERVICES_ONLY">Services only (procedures, appointments)</MenuItem>
          <MenuItem value="PRODUCTS_ONLY">Products only (medications, vaccinations)</MenuItem>
          <MenuItem value="NONE">No tax (pricing is tax-inclusive)</MenuItem>
        </TextField>

        <TextField
          label="Default Payment Due"
          type="number"
          value={formData.defaultPaymentDays}
          onChange={(e) => setFormData({ ...formData, defaultPaymentDays: e.target.value })}
          size="small"
          fullWidth
          inputProps={{ min: 0, max: 365, step: 1 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">days</InputAdornment>,
          }}
          helperText="Set to 0 for due on receipt."
          {...readOnlyProps}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          // Clinic defaults aren't an offline-writable entity — save online only
          // (the payment-types section below shows its own offline notice).
          disabled={!isEditable || isOffline}
        >
          Save Changes
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Default Invoice Note
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every new invoice starts with this note, however it is made, and staff can change or
        delete it on any invoice. Use it for your Venmo or Cash App link, &quot;Checks payable
        to&quot;, or a thank-you line. Links are clickable on the PDF and in the invoice email.
      </Typography>

      <Box sx={{ maxWidth: 650 }}>
        <TextField
          label="Default invoice note"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          size="small"
          inputProps={{ maxLength: 1000 }}
          placeholder="Pay by Venmo: https://venmo.com/u/yourclinic"
          helperText="Changing it affects new invoices only. Invoices you already made keep their note."
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => noteMutation.mutate(noteDraft)}
            // !invoiceSettings: a click before the query hydrates would save '' and wipe the note.
            disabled={isOffline || noteMutation.isLoading || !invoiceSettings}
          >
            {noteMutation.isLoading ? 'Saving…' : 'Save Invoice Note'}
          </Button>
        </Box>
      </Box>

      {/* Hidden for clinics that opted out of the hospital module entirely —
          they have no treatment sheets for this switch to act on. */}
      {clinicData?.hospitalModuleEnabled !== false && (
        <>
          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Hospital Treatments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When this is on, every treatment your team records on a patient's treatment sheet is
            added to that admission's invoice as it is given — one invoice per stay, so nothing has
            to be written up again at discharge. Only treatments given from the moment you switch it
            on are billed: earlier stays are never charged retroactively. Turning it off stops new
            charges, and anything already invoiced stays on the invoice.
          </Typography>

          <Box sx={{ maxWidth: 650 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoBillOn}
                  onChange={(e) => autoBillMutation.mutate(e.target.checked)}
                  disabled={isOffline || autoBillMutation.isLoading}
                />
              }
              label="Automatically bill hospital treatments"
            />
            {autoBillOn && autoBillOnSince && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                On since {autoBillOnSince}
              </Typography>
            )}
          </Box>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Monthly Statements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When enabled, every client with an outstanding balance is emailed an account
        statement on the 1st of each month (clinic time), listing their open invoices
        grouped by horse. Clients with no outstanding balance are skipped; clients
        without an email address get a draft statement you can print or mail.
      </Typography>

      <Box sx={{ maxWidth: 650 }}>
        <FormControlLabel
          control={
            <Switch
              checked={stmtForm.enabled}
              onChange={(e) => setStmtForm({ ...stmtForm, enabled: e.target.checked })}
              disabled={isOffline}
            />
          }
          label="Email monthly statements automatically"
        />
        <TextField
          label="Cover letter"
          value={stmtForm.coverLetter}
          onChange={(e) => setStmtForm({ ...stmtForm, coverLetter: e.target.value })}
          multiline
          minRows={4}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 5000 }}
          helperText="Optional. Included as the first page of every statement PDF and in the statement email. Plain text — blank lines start a new paragraph."
        />
        <TextField
          label="Tax registration line"
          value={stmtForm.taxNumber}
          onChange={(e) => setStmtForm({ ...stmtForm, taxNumber: e.target.value })}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 40 }}
          helperText={'Optional. Printed exactly as typed under the clinic address on every invoice and statement PDF — include your own label, e.g. "GST # 123456789 RT0001", "VAT No. DE123456789", or "EIN 12-3456789". Leave blank if not applicable.'}
        />
        <TextField
          label="Payment terms note"
          value={stmtForm.terms}
          onChange={(e) => setStmtForm({ ...stmtForm, terms: e.target.value })}
          multiline
          minRows={2}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 500 }}
          helperText={'Optional. Shown under the totals on statement PDFs, e.g. "Payment due upon receipt. 1.5% monthly interest on overdue accounts." Display-only — no interest is calculated.'}
        />
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={() =>
              stmtMutation.mutate({
                monthlyStatementsEnabled: stmtForm.enabled,
                statementCoverLetter: stmtForm.coverLetter,
                taxRegistrationNumber: stmtForm.taxNumber,
                statementTerms: stmtForm.terms,
              })
            }
            // !stmtSettings: a click before the settings query hydrates would
            // save the form's empty defaults and wipe the stored letter/terms.
            disabled={isOffline || stmtMutation.isLoading || !stmtSettings}
          >
            {stmtMutation.isLoading ? 'Saving…' : 'Save Statement Settings'}
          </Button>
        </Box>
      </Box>

      {isEditable && (
        <>
          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Refund approval
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Approving a Stripe refund takes an owner's or admin's 6-digit PIN, so a rep can stay
            logged in while a manager approves. The log records who processed the refund and who
            approved it. Each person sets their own PIN in Account settings.
          </Typography>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      <PaymentTypesSettings />
    </Box>
  );
};

export default BillingSettings;
